import { Text } from "~/components/Text"
import { useEffect, useMemo, useState } from "react"
import { Button, Flex, Modal, Segmented, Table, Typography } from "antd"
import { Alert } from "~/components/Alert"
import type { TableProps } from "antd"
import { AnalyticsAction } from "~/components/Analytics"
import {
  POINT_ICON_URL,
  WalletRechargeType,
  WalletRecordType,
} from "~/common/constants"
import { isUnauthorizedRequestError } from "~/common/request"
import {
  getPointRecordDescription,
  POINT_RECORD_TYPE_OPTIONS,
  WALLET_RECHARGE_TYPE_LABEL,
} from "~/common/billing/labels"
import { getWalletBalanceSummary, listWalletRecords } from "~/api/user"
import styles from "./index.css"

const { Title } = Typography

export type PointDetailModalProps = {
  open: boolean
  onCancel: () => void
  onRecharge: () => void
}

const pointRecordPageSize = 10

const walletRechargeTypeOrder = [
  WalletRechargeType.registrationGift,
  WalletRechargeType.levelInclude,
  WalletRechargeType.walletPackage,
  WalletRechargeType.staff,
] as const

const isIncomeRecord = (record: API.User.WalletRecordItem) => {
  return record.record_type === WalletRecordType.income
}

const formatRecordTime = (timestamp: number) => {
  return new Date(timestamp * 1000).toLocaleString("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour12: false,
  })
}

const buildPointBuckets = (
  summary: API.User.WalletBalanceSummaryResponse | undefined,
) => {
  const detailMap = new Map(
    (summary?.balance_detail || []).map((item) => [
      item.wallet_recharge_type,
      item,
    ]),
  )

  return walletRechargeTypeOrder.flatMap((key) => {
    const detail = detailMap.get(key)
    const value = detail?.available_amount || 0

    if (key === WalletRechargeType.staff && value === 0) return []

    return [
      {
        expireText: value > 0 ? detail?.desc || "" : "",
        key,
        label: WALLET_RECHARGE_TYPE_LABEL[key],
        value,
      },
    ]
  })
}

export const PointDetailModal = ({
  open,
  onCancel,
  onRecharge,
}: PointDetailModalProps) => {
  const [pointRecordType, setPointRecordType] = useState<WalletRecordType>(
    WalletRecordType.income,
  )
  const [recordPage, setRecordPage] = useState(1)
  const [summary, setSummary] =
    useState<API.User.WalletBalanceSummaryResponse>()
  const [records, setRecords] = useState<API.User.WalletRecordItem[]>([])
  const [recordsTotal, setRecordsTotal] = useState(0)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [recordsLoading, setRecordsLoading] = useState(false)
  const [summaryError, setSummaryError] = useState("")
  const [recordsError, setRecordsError] = useState("")

  const pointBuckets = useMemo(() => {
    return buildPointBuckets(summary)
  }, [summary])
  const pointBalance = summary?.total_available_amount || 0
  const pointColumns: TableProps<API.User.WalletRecordItem>["columns"] = [
    {
      title: "时间",
      dataIndex: "record_time",
      render: (value: number) => formatRecordTime(value),
    },
    {
      title: "积点",
      dataIndex: "amount",
      render: (value: number, record) => (
        <Text type={isIncomeRecord(record) ? "success" : "danger"}>
          {isIncomeRecord(record) ? `+${value}` : -value}
        </Text>
      ),
    },
    {
      title: "说明",
      dataIndex: "remark",
      render: (_value: string, record) => (
        <Flex
          align="center"
          gap={8}
        >
          <span
            className={
              isIncomeRecord(record) ? styles.incomeDot : styles.expenseDot
            }
          />
          <span>{getPointRecordDescription(record)}</span>
        </Flex>
      ),
    },
  ]
  useEffect(() => {
    if (!open) return

    let active = true

    setSummaryLoading(true)
    setSummaryError("")

    getWalletBalanceSummary()
      .then((response) => {
        if (!active) return

        setSummary(response.data || undefined)
      })
      .catch((error: unknown) => {
        if (!active) return

        setSummary(undefined)
        if (isUnauthorizedRequestError(error)) return

        setSummaryError(
          error instanceof Error ? error.message : "积点余额加载失败",
        )
      })
      .finally(() => {
        if (!active) return

        setSummaryLoading(false)
      })

    return () => {
      active = false
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    let active = true

    setRecordsLoading(true)
    setRecordsError("")

    const fetchRecords = async () => {
      const response = await listWalletRecords({
        record_type: pointRecordType,
        page: recordPage - 1,
        page_size: pointRecordPageSize,
      })

      return {
        items: response.data?.items || [],
        total: response.data?.total || 0,
      }
    }

    fetchRecords()
      .then(({ items, total }) => {
        if (!active) return

        setRecords(items)
        setRecordsTotal(total)
      })
      .catch((error: unknown) => {
        if (!active) return

        setRecords([])
        setRecordsTotal(0)
        if (isUnauthorizedRequestError(error)) return

        setRecordsError(
          error instanceof Error ? error.message : "积点记录加载失败",
        )
      })
      .finally(() => {
        if (!active) return

        setRecordsLoading(false)
      })

    return () => {
      active = false
    }
  }, [open, pointRecordType, recordPage])

  const handleRecordTypeChange = (value: WalletRecordType) => {
    setPointRecordType(value)
    setRecordPage(1)
  }

  return (
    <Modal
      centered
      open={open}
      title={
        <Flex
          align="center"
          gap={12}
        >
          <img
            alt=""
            width={32}
            src={POINT_ICON_URL}
          />
          <Title level={4}>积点明细</Title>
        </Flex>
      }
      width={760}
      onCancel={onCancel}
      footer={null}
    >
      <Flex
        vertical
        gap={16}
      >
        <Flex
          className={styles.pointSummary}
          align="center"
          justify="space-between"
        >
          <Flex
            className={styles.pointBalanceBlock}
            vertical
            gap={8}
          >
            <Text type="secondary">积点余额</Text>
            <Title
              className={styles.pointSummaryValue}
              level={3}
            >
              {pointBalance}
            </Title>
          </Flex>
          <Title
            type="secondary"
            level={5}
          >
            =
          </Title>
          {pointBuckets.map((bucket, index) => (
            <>
              {index > 0 ? (
                <Title
                  type="secondary"
                  level={5}
                  key={bucket.key}
                >
                  +
                </Title>
              ) : null}
              <Flex
                className={styles.pointBucket}
                vertical
                gap={6}
                align="center"
                key={bucket.key + "pointBucket"}
              >
                <Text type="secondary">{bucket.label}</Text>
                <Title
                  level={3}
                  type={bucket.value > 0 ? undefined : "secondary"}
                >
                  {bucket.value}
                </Title>
                {bucket.expireText ? (
                  <Text
                    size="s"
                    type="secondary"
                  >
                    {bucket.expireText}
                  </Text>
                ) : null}
              </Flex>
            </>
          ))}
        </Flex>
        {summaryError ? (
          <Alert
            title={summaryError}
            showIcon
            level="strong"
          />
        ) : null}
        {!summaryLoading && !summaryError && pointBalance <= 0 ? (
          <Alert
            showIcon
            title={
              <Flex
                align="center"
                justify="space-between"
              >
                <Flex
                  align="center"
                  gap={10}
                >
                  <Text>您的积点已用完</Text>
                </Flex>
                <AnalyticsAction
                  action="点击充值"
                  category="账户体系"
                  prefix="充值积分"
                >
                  <Button
                    type="primary"
                    onClick={onRecharge}
                  >
                    立即充值
                  </Button>
                </AnalyticsAction>
              </Flex>
            }
          />
        ) : null}
        <Segmented
          style={{ width: "fit-content" }}
          options={[...POINT_RECORD_TYPE_OPTIONS]}
          value={pointRecordType}
          onChange={(value) =>
            handleRecordTypeChange(value as WalletRecordType)
          }
        />
        {recordsError ? (
          <Alert
            title={recordsError}
            showIcon
            level="strong"
          />
        ) : null}
        <Table
          size="small"
          columns={pointColumns}
          dataSource={records}
          loading={recordsLoading}
          pagination={{
            current: recordPage,
            pageSize: pointRecordPageSize,
            showSizeChanger: false,
            total: recordsTotal,
            onChange: setRecordPage,
          }}
          rowKey="record_id"
        />
      </Flex>
    </Modal>
  )
}
